import { ChevronDown, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Column } from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { CurrencyPaymentItem } from '@/interfaces/billing'
import { exportExcel, formatCurrency } from '@/utils/helpers'

import { useTableContext } from './TableContext'
import {
  CurrencyPaymentStatusFlags,
  resolveStatus,
  statusConfig,
} from '@/components/misc/badges/CurrencyPaymentStatusBadge'

export const HeaderActions = ({
  columns,
}: {
  columns: Column<CurrencyPaymentItem, unknown>[]
}) => {
  // Use the context
  const {
    currentPage,
    pageSize,
    isLoading,
    refetch,
    setPageSize,
    currencyPaymentsData,
  } = useTableContext()

  const [isMobileView, setIsMobileView] = useState(false)

  // Export action for Excel - respects column visibility
  const exportToExcelAction = () => {
    if (
      !currencyPaymentsData?.items ||
      currencyPaymentsData.items.length === 0
    ) {
      return
    }

    // Get only visible columns
    const visibleColumns = columns.filter((column) => column.getIsVisible())

    const getCurrencyPaymentStatusLabel = (
      flags: CurrencyPaymentStatusFlags
    ): string => {
      const status = resolveStatus(flags)
      return statusConfig[status].label
    }

    const dataToExport: Record<string, string | number>[] =
      currencyPaymentsData.items.map((item: CurrencyPaymentItem) => {
        const exportData: Record<string, string | number> = {}

        visibleColumns.forEach((column) => {
          const columnId = column.id
          const header =
            typeof column.columnDef.header === 'string'
              ? column.columnDef.header
              : columnId

          switch (columnId) {
            case 'paymentNumber':
              exportData[header] = item.paymentNumber
                ? `P-${item.paymentNumber}`
                : '-'
              break

            case 'amount':
              exportData[header] = item.amount
                ? formatCurrency(parseFloat(item.amount), item.codeCurrency)
                : '-'
              break

            case 'paymentGateway':
              exportData[header] = item.paymentGateway?.name || '-'
              break

            case 'status':
              exportData[header] = getCurrencyPaymentStatusLabel({
                isPrepaidPayment: item.isPrepaidPayment,
                isARefund: item.isARefund,
                paymentConfirmed: item.paymentConfirmed,
                isPartialPayment: item.isPartialPayment,
              })
              break
            case 'observations':
              exportData[header] = item.observations || '-'
              break
          }
        })

        return exportData
      })

    const fileName = `pagos-recibidos-${new Date().toISOString().split('T')[0]}`
    exportExcel(dataToExport, fileName)
  }

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // For mobile: stacked layout
  if (isMobileView) {
    return (
      <div className="flex-col gap-4 p-4">
        <div className="mb-4 flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              aria-label="Refrescar tabla"
              className="h-8 w-8"
              disabled={isLoading}
              onClick={refetch}
              size="icon"
              variant="ghost"
            >
              {isLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Exportar"
                  className="h-8 gap-1"
                  size="sm"
                  variant="outline"
                >
                  <span>Exportar</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => exportToExcelAction()}
                >
                  <span>Excel</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Mostrar columnas"
                  className="h-8 gap-1"
                  size="sm"
                  variant="outline"
                >
                  <span>Resultados por página: {pageSize}</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Resultados por página</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(20)}
                >
                  <span>20</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(50)}
                >
                  <span>50</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(100)}
                >
                  <span>100</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(500)}
                >
                  <span>500</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="bg-muted-foregrohover:und hover:cursor/10-pointer"
                  onClick={() => setPageSize(1000)}
                >
                  <span>1000</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    )
  }

  // For desktop: layout with creation buttons on left, pagination in middle, and utility buttons on right
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 p-4">
      {/*Left - Pagination info */}
      <div className="order-2 min-w-[300px] text-center text-sm text-muted-foreground sm:order-1 sm:text-left">
        <Button
          aria-label="Refrescar tabla"
          className="h-8 w-8"
          disabled={isLoading}
          onClick={refetch}
          size="icon"
          variant="ghost"
        >
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
        <span className="min-h-[20px] min-w-[300px] text-sm text-muted-foreground">
          {isLoading ? (
            <Skeleton className="h-4 w-[300px]" />
          ) : currencyPaymentsData?.totalItems ? (
            <>
              Mostrando {(currentPage - 1) * pageSize + 1} al{' '}
              {Math.min(
                currentPage * pageSize,
                currencyPaymentsData.totalItems
              )}{' '}
              de {currencyPaymentsData.totalItems} resultados
            </>
          ) : (
            'No hay resultados'
          )}
        </span>
      </div>
      {/* Right - Export and columns buttons */}
      <div className="order-2 flex items-center gap-2 xl:order-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label="Exportar datos"
              className="h-8 gap-1"
              size="sm"
              variant="outline"
            >
              <span>Exportar</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => exportToExcelAction()}
            >
              <span>Excel</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label="Mostrar columnas"
              className="h-8 gap-1"
              size="sm"
              variant="outline"
            >
              <span>Resultados por página: {pageSize}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Resultados por página</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(20)}
            >
              <span>20</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(50)}
            >
              <span>50</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(100)}
            >
              <span>100</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(500)}
            >
              <span>500</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(1000)}
            >
              <span>1000</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
